import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Portal,
  Stack,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MicIcon from '@mui/icons-material/Mic';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const CHANNELS = [
  {
    channel: 'web',
    label: 'Web Chat',
    icon: <MessageOutlinedIcon fontSize="small" />,
    description: 'Embedded widget and dashboard preview.',
    accent: 'from-sky-500/20 to-cyan-400/10',
  },
  {
    channel: 'whatsapp',
    label: 'WhatsApp',
    icon: <WhatsAppIcon fontSize="small" />,
    description: 'Meta Cloud API webhook and delivery.',
    accent: 'from-emerald-500/20 to-lime-400/10',
  },
  {
    channel: 'voice',
    label: 'Voice',
    icon: <MicIcon fontSize="small" />,
    description: 'Speech-to-text and text-to-speech flows.',
    accent: 'from-violet-500/20 to-fuchsia-400/10',
  },
];

const statusTone = {
  connected: 'success',
  not_configured: 'warning',
  offline: 'default',
};

/** Purpose: configure connected communication channels. Why: bot publishing and inbound routing depend on channel-specific credentials and settings. */
export default function Channels() {
  const { selectedBot, selectedEnv } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [overview, setOverview] = useState({ channels: [], channelSettings: {} });
  const [activeChannel, setActiveChannel] = useState('web');
  const [testMessage, setTestMessage] = useState('hello');
  const [testResult, setTestResult] = useState(null);
  const [templateTo, setTemplateTo] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('en_US');
  const [templateBody, setTemplateBody] = useState('');
  const [headerHost, setHeaderHost] = useState(() => (
    typeof document !== 'undefined' ? document.getElementById('channels-header-actions') : null
  ));

  useEffect(() => {
    if (!headerHost && typeof document !== 'undefined') {
      const element = document.getElementById('channels-header-actions');
      if (element) setHeaderHost(element);
    }
  }, [headerHost]);

  /** Purpose: Resolve channel map for the channels module. Why: The surrounding operation uses this value to make its decision. */
  const channelMap = useMemo(() => {
    const map = new Map();
    (overview.channels || []).forEach((channel) => map.set(channel.channel, channel));
    return map;
  }, [overview.channels]);

  /** Purpose: Load fetch overview for the channels module. Why: The selected view needs current data to render or continue processing. */
  const fetchOverview = useCallback(async () => {
    if (!selectedBot) {
      setOverview({ channels: [], channelSettings: {} });
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/v1/channels', {
        params: { botId: selectedBot, env: selectedEnv || 'dev' },
      });
      setOverview({
        channels: response.data.channels || [],
        channelSettings: response.data.channelSettings || {},
      });
      const firstAvailable = response.data.channels?.find((item) => item.enabled)?.channel || 'web';
      setActiveChannel(firstAvailable);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load channel status');
    } finally {
      setLoading(false);
    }
  }, [selectedBot, selectedEnv]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  /** Purpose: Handle handle toggle in the channels module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleToggle = async (channel, enabled) => {
    if (!selectedBot) return;

    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.put('/api/v1/channels/settings', {
        botId: selectedBot,
        env: selectedEnv || 'dev',
        channel,
        enabled,
      });
      setOverview({
        channels: response.data.channels || [],
        channelSettings: response.data.channelSettings || {},
      });
      setMessage(`${channel.toUpperCase()} channel ${enabled ? 'enabled' : 'disabled'} successfully.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update channel settings');
    } finally {
      setSaving(false);
    }
  };

  /** Purpose: Handle run test in the channels module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const runTest = async () => {
    if (!selectedBot) return;

    setSaving(true);
    setTestResult(null);
    setMessage('');
    setError('');
    try {
      const response = await axios.post('/api/v1/channels/test', {
        botId: selectedBot,
        env: selectedEnv || 'dev',
        channel: activeChannel,
        message: testMessage,
        sessionId: `channel-test-${activeChannel}-${Date.now()}`,
        debug: true,
      });
      setTestResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Channel test failed');
    } finally {
      setSaving(false);
    }
  };

  /** Purpose: Handle send template in the channels module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const sendTemplate = async () => {
    if (!selectedBot || !templateTo || !templateName) return;

    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post('/api/v1/channels/whatsapp/template', {
        botId: selectedBot,
        env: selectedEnv || 'dev',
        to: templateTo,
        templateName,
        languageCode: templateLanguage,
        components: templateBody
          ? [
              {
                type: 'body',
                parameters: [
                  { type: 'text', text: templateBody },
                ],
              },
            ]
          : [],
      });
      setMessage(response.data?.message || 'Template sent successfully.');
    } catch (err) {
      // Meta Graph errors use { error: { message, type, code, ... } }. Keep
      // provider error objects out of React children, which would crash the
      // page and appear as a blank screen after a failed template send.
      const apiError = err.response?.data?.error;
      const apiMessage = typeof apiError === 'string' ? apiError : apiError?.message;
      const responseMessage = err.response?.data?.message;
      setError(
        (typeof apiMessage === 'string' && apiMessage)
        || (typeof responseMessage === 'string' && responseMessage)
        || err.message
        || 'Failed to send template',
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedChannel = channelMap.get(activeChannel) || null;

  if (!selectedBot) {
    return (
      <Alert severity="info">
        Select a bot first to manage channels.
      </Alert>
    );
  }

  return (
    <Box className="space-y-4">
      <Snackbar
        open={Boolean(message || error)}
        autoHideDuration={7000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setMessage('');
          setError('');
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '76px !important', zIndex: (theme) => theme.zIndex.snackbar + 1 }}
      >
        <Alert
          severity={error ? 'error' : 'success'}
          variant="filled"
          onClose={() => { setMessage(''); setError(''); }}
          sx={{ width: 'min(720px, calc(100vw - 32px))', boxShadow: 6 }}
        >
          {error || message}
        </Alert>
      </Snackbar>

      <Portal container={headerHost} disablePortal={!headerHost}>
        <Chip
          size="small"
          label={loading ? 'Loading channels…' : 'Live status'}
          color={loading ? 'default' : 'info'}
          variant="outlined"
          icon={loading ? undefined : <CheckCircleIcon fontSize="small" />}
          sx={{ height: 28, fontWeight: 600 }}
        />
      </Portal>

      <Grid container spacing={2}>
        {CHANNELS.map((channel) => {
          const status = channelMap.get(channel.channel);
          const enabled = Boolean(status?.enabled ?? overview.channelSettings?.[channel.channel]);
          const state = status?.status || (enabled ? 'connected' : 'offline');

          return (
            <Grid item xs={12} md={4} key={channel.channel}>
              <Card className="h-full rounded-3xl border border-slate-200/80 bg-white/90 shadow-sm dark:border-white/10 dark:bg-slate-950/80">
                <CardContent className="space-y-4">
                  <Box className={`rounded-3xl border border-slate-200/70 bg-gradient-to-br ${channel.accent} p-4 dark:border-white/10`}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/70 text-slate-900 dark:bg-slate-950/70 dark:text-white">
                          {channel.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={800}>
                            {channel.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {channel.description}
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip label={state} color={statusTone[state] || 'default'} size="small" />
                    </Stack>
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={(event) => handleToggle(channel.channel, event.target.checked)}
                        disabled={saving}
                      />
                    }
                    label={`Enable ${channel.label}`}
                  />

                  <Divider />

                  <Typography variant="body2" color="text.secondary">
                    {channel.channel === 'whatsapp'
                      ? 'Manage Meta WhatsApp credentials and webhook details in Settings. Secrets remain masked.'
                      : channel.channel === 'voice'
                        ? 'Use STT/TTS providers to convert speech to text and back.'
                        : 'Web channel is available in dashboard and widget integrations.'}
                  </Typography>

                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={channel.channel === 'voice' ? <MicIcon /> : <ElectricBoltIcon />}
                    onClick={() => setActiveChannel(channel.channel)}
                    sx={{ borderRadius: 999 }}
                  >
                    Test {channel.label}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {activeChannel === 'whatsapp' ? (
        <Paper className="rounded-3xl border border-slate-200/80 bg-white/90 p-4 shadow-sm dark:border-white/10 dark:bg-slate-950/80">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={800}>WhatsApp connection</Typography>
              <Typography variant="body2" color="text.secondary">Configuration and health for the selected bot environment. Credentials are never shown here.</Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" component="a" href="/settings">Integration settings</Button>
              <Button variant="contained" component="a" href="/campaign-hub/templates">Open Campaign Hub</Button>
            </Stack>
          </Stack>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              ['Provider', selectedChannel?.integrationDetails?.provider || '—'],
              ['WABA ID', selectedChannel?.integrationDetails?.wabaId || 'Not configured'],
              ['Phone Number ID', selectedChannel?.integrationDetails?.phoneNumberId || 'Not configured'],
              ['Display number', 'Not available in saved configuration'],
              ['Webhook verification', selectedChannel?.integrationDetails?.webhookVerificationConfigured ? 'Server secrets configured' : 'Not configured'],
              ['Templates', `${selectedChannel?.integrationDetails?.templateCount || 0} synced`],
              ['Last template sync', selectedChannel?.integrationDetails?.lastTemplateSyncAt ? new Date(selectedChannel.integrationDetails.lastTemplateSyncAt).toLocaleString() : 'Never'],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : null}

      <Paper className="rounded-3xl border border-slate-200/80 bg-white/90 p-4 shadow-sm dark:border-white/10 dark:bg-slate-950/80">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Tabs value={activeChannel} onChange={(_, value) => setActiveChannel(value)}>
            {CHANNELS.map((channel) => (
              <Tab key={channel.channel} value={channel.channel} label={channel.label} />
            ))}
          </Tabs>
          <Chip
            label={selectedChannel?.status || 'status unknown'}
            color={statusTone[selectedChannel?.status] || 'default'}
            variant="outlined"
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          {activeChannel === 'whatsapp' ? (
            <Paper variant="outlined" className="rounded-2xl border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                WhatsApp template send
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Recipient" value={templateTo} onChange={(e) => setTemplateTo(e.target.value)} placeholder="919999999999" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Template name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="hello_world" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Language code" value={templateLanguage} onChange={(e) => setTemplateLanguage(e.target.value)} placeholder="en_US" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Body parameter text" value={templateBody} onChange={(e) => setTemplateBody(e.target.value)} placeholder="Optional text parameter" helperText="Quick test supports one text value for the body only. Templates with multiple variables, media headers, or button values need a complete mapping in Campaign Hub → Notifications or Campaigns." />
                </Grid>
              </Grid>
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button type="button" variant="contained" onClick={sendTemplate} disabled={saving || !templateTo || !templateName} sx={{ borderRadius: 999 }}>
                  {saving ? 'Sending template…' : 'Send template'}
                </Button>
              </Stack>
            </Paper>
          ) : null}
          <TextField
            label={`Test ${selectedChannel?.label || 'channel'} message`}
            value={testMessage}
            onChange={(event) => setTestMessage(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={fetchOverview} disabled={loading || saving} sx={{ borderRadius: 999 }}>
              Refresh status
            </Button>
            <Button variant="contained" onClick={runTest} disabled={loading || saving || !testMessage.trim()} sx={{ borderRadius: 999 }}>
              Run test
            </Button>
          </Stack>
          <Paper variant="outlined" className="rounded-2xl border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Response preview
            </Typography>
            {testResult ? (
              <pre className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Run a test to preview the formatted channel response.
              </Typography>
            )}
          </Paper>
        </Stack>
      </Paper>
    </Box>
  );
}
